import { useState, useEffect, useCallback } from 'react';
import { invoiceSettingsApi } from '@/lib/api';
import { useToast } from '@/context/ToastContext';

export interface InvoiceSettings {
  id: number;
  prefix: string;
  format_pattern: string;
  current_number: number;
  number_padding: number;
  reset_yearly: boolean;
  separator: string;
  terms_and_conditions: string | null;
  footer_notes: string | null;
  created_at: string;
  updated_at: string;
}

export function useInvoiceSettings() {
  const [settings, setSettings] = useState<InvoiceSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<string>('');
  const { showToast } = useToast();

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await invoiceSettingsApi.get();
      if (response.success) {
        setSettings(response.data);
        setPreview(response.preview);
      }
    } catch (error) {
      console.error('Failed to fetch invoice settings:', error);
      showToast('Failed to load invoice settings', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const updateSettings = async (data: Partial<InvoiceSettings>) => {
    try {
      const response = await invoiceSettingsApi.update(data);
      if (response.success) {
        setSettings(response.data);
        setPreview(response.preview);
        showToast('Invoice settings updated successfully', 'success');
        return true;
      }
    } catch (error: any) {
      console.error('Failed to update invoice settings:', error);
      showToast(error.message || 'Failed to update invoice settings', 'error');
      return false;
    }
  };

  const previewNextNumber = (): string => {
    if (!settings) return '';
    
    const year = new Date().getFullYear();
    const month = String(new Date().getMonth() + 1).padStart(2, '0');
    const nextNumber = settings.current_number + 1;
    const number = String(nextNumber).padStart(settings.number_padding, '0');
    
    let result = settings.format_pattern
      .replace('{PREFIX}', settings.prefix)
      .replace('{YEAR}', String(year))
      .replace('{MONTH}', month)
      .replace('{NUMBER}', number);
    
    return result;
  };

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return {
    settings,
    loading,
    preview,
    fetchSettings,
    updateSettings,
    previewNextNumber,
  };
}
